// ═══════════════════════════════════════════════════════
// Schoolizy — Composants Pagination + Recherche avancée
// Usage :
//   const { filtered, search, setSearch, page, setPage, paginated, total, Pagination } = usePagination(rows, { pageSize: 5, searchKeys: ['nom', 'matricule'] })
// ═══════════════════════════════════════════════════════
import { useMemo, useState } from 'react';

// ── Hook principal ────────────────────────────────────
export function usePagination(rows = [], { pageSize = 5, searchKeys = [] } = {}) {
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [filters, setFilters] = useState({});

  // Filtrage
  const filtered = useMemo(() => {
    let data = rows;
    // Recherche textuelle
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((row) =>
        searchKeys.some((key) => {
          const val = key.split('.').reduce((o, k) => o?.[k], row);
          return String(val ?? '').toLowerCase().includes(q);
        })
      );
    }
    // Filtres additionnels
    Object.entries(filters).forEach(([key, val]) => {
      if (!val) return;
      data = data.filter((row) => {
        const rowVal = key.split('.').reduce((o, k) => o?.[k], row);
        return String(rowVal ?? '') === String(val);
      });
    });
    return data;
  }, [rows, search, filters]);

  // Reset page quand la recherche change
  const filteredWithReset = useMemo(() => {
    setPage(1);
    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, JSON.stringify(filters)]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() =>
    filtered.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filtered, safePage, pageSize]
  );

  const PaginationBar = () => (
    <PaginationComponent
      page={safePage} totalPages={totalPages} total={total}
      pageSize={pageSize} onChange={setPage}
    />
  );

  return {
    search, setSearch,
    filters, setFilters,
    filtered,
    paginated,
    page: safePage, setPage,
    total,
    totalPages,
    Pagination: PaginationBar,
  };
}

// ── Barre de pagination ───────────────────────────────
export function PaginationComponent({ page, totalPages, total, pageSize, onChange }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  // Pages à afficher (max 5 boutons)
  const pages = [];
  const delta = 2;
  for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
    pages.push(i);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderTop: '0.5px solid var(--line)', background: 'var(--surface-soft)', flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>
        {total === 0 ? 'Aucun résultat' : `${from}–${to} sur ${total} résultat${total > 1 ? 's' : ''}`}
      </span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <PageBtn label="«" disabled={page === 1} onClick={() => onChange(1)} />
        <PageBtn label="‹" disabled={page === 1} onClick={() => onChange(page - 1)} />
        {pages[0] > 1 && <span style={{ padding: '0 4px', color: 'var(--muted)', fontSize: 12 }}>…</span>}
        {pages.map((p) => (
          <PageBtn key={p} label={String(p)} active={p === page} onClick={() => onChange(p)} />
        ))}
        {pages[pages.length - 1] < totalPages && <span style={{ padding: '0 4px', color: 'var(--muted)', fontSize: 12 }}>…</span>}
        <PageBtn label="›" disabled={page === totalPages} onClick={() => onChange(page + 1)} />
        <PageBtn label="»" disabled={page === totalPages} onClick={() => onChange(totalPages)} />
      </div>
    </div>
  );
}

function PageBtn({ label, active, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        minWidth: 32, height: 32, borderRadius: 6, border: '0.5px solid',
        borderColor: active ? 'var(--primary)' : 'var(--line)',
        background: active ? 'var(--primary)' : 'var(--surface)',
        color: active ? '#fff' : disabled ? 'var(--muted-light)' : 'var(--ink)',
        cursor: disabled ? 'default' : 'pointer',
        fontSize: 13, fontWeight: active ? 600 : 400,
        opacity: disabled ? 0.4 : 1,
        padding: '0 6px',
      }}>
      {label}
    </button>
  );
}

// ── Barre de recherche avancée ────────────────────────
export function SearchBar({ value, onChange, placeholder = 'Rechercher…', children, total, filtered }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 12px', background: 'var(--surface-soft)', borderBottom: '0.5px solid var(--line)' }}>
      <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 14 }}>🔍</span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ paddingLeft: 32, height: 36, width: '100%' }}
        />
        {value && (
          <button onClick={() => onChange('')}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 0, cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}>
            ✕
          </button>
        )}
      </div>
      {children}
      {total !== undefined && filtered !== undefined && filtered < total && (
        <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          {filtered} / {total} résultats
        </span>
      )}
    </div>
  );
}
